import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { FiShoppingCart, FiMinus, FiPlus, FiX, FiChevronDown, FiChevronUp, FiSearch, FiFilter, FiChevronRight, FiBell, FiArrowLeft } from 'react-icons/fi';

function QRMenu() {
  const { tableId, tenantSlug } = useParams();
  const { alert, confirm } = useModal();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // null = category view, categoryId = dish view
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    phone: ''
  });
  // Guest popup disabled: start closed and never auto-open
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [vegFilter, setVegFilter] = useState('all'); // 'all', 'veg', 'non-veg'
  const [tableNumber, setTableNumber] = useState(null);
  const [loadingTable, setLoadingTable] = useState(true);
  const [callingWaiter, setCallingWaiter] = useState(false);
  const [waiterCalled, setWaiterCalled] = useState(false);
  const [qrOrderingEnabled, setQrOrderingEnabled] = useState(true);
  const [qrConfigLoaded, setQrConfigLoaded] = useState(false);
  const [qrFormAllowSkip, setQrFormAllowSkip] = useState(true); // Default: allow skip
  const [qrMenuShowPhotos, setQrMenuShowPhotos] = useState(true); // Default: show photos
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState('');
  const [restaurantLogoUrl, setRestaurantLogoUrl] = useState('');
  const [restaurantLogoShape, setRestaurantLogoShape] = useState('circle');
  const [lastOrderSummary, setLastOrderSummary] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [pendingScrollItemId, setPendingScrollItemId] = useState(null);

  const orderingAllowed = qrOrderingEnabled && Boolean(tableId);

  useEffect(() => {
    fetchMenu();
  }, [tableId, tenantSlug]);

  useEffect(() => {
    fetchQrConfig();
  }, [tableId, tenantSlug]);

  useEffect(() => {
    if (!qrConfigLoaded) return;
    if (!orderingAllowed) {
      setShowGuestModal(false);
      setShowCart(false);
    }
  }, [orderingAllowed, qrConfigLoaded]);

  // Previously used to auto-hide the guest popup based on config.
  // Popup is now permanently disabled, so we don't toggle it here.
  useEffect(() => {
    if (!qrConfigLoaded) return;
  }, [qrFormAllowSkip, qrConfigLoaded]);

  // On initial load, hydrate order summary from localStorage (per table) for this device
  useEffect(() => {
    if (!tableId) return;
    try {
      const stored = localStorage.getItem(`qrOrderHistory_${tableId}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && Array.isArray(parsed.items) && parsed.items.length > 0) {
          setLastOrderSummary(parsed);
        }
      }
    } catch (e) {
      console.warn('Failed to read stored QR order history:', e);
    }
  }, [tableId]);

  useEffect(() => {
    if (tableId) {
      fetchTableNumber();
      fetchExistingOrders();
    }
  }, [tableId]);
  
  // Retry fetching table number if it fails initially
  useEffect(() => {
    if (tableId && !tableNumber && !loadingTable) {
      const retryTimer = setTimeout(() => {
        fetchTableNumber();
      }, 2000);
      return () => clearTimeout(retryTimer);
    }
  }, [tableId, tableNumber, loadingTable]);

  const fetchQrConfig = async () => {
    try {
      if (!tableId && !tenantSlug) {
        setQrConfigLoaded(true);
        return;
      }
      const params = tableId ? { tableId } : { tenantSlug };
      const response = await api.get('/api/public/tenant/modules', { params });
      const enabled = response.data?.qrOrderingEnabled !== false;
      setQrOrderingEnabled(enabled);
      // Get QR form setting
      const allowSkip = response.data?.qrMenuFormAllowSkip !== false;
      setQrFormAllowSkip(allowSkip);
      // Get QR photo setting
      const showPhotos = response.data?.qrMenuShowPhotos !== false;
      setQrMenuShowPhotos(showPhotos);
      // Restaurant logo for header
      if (response.data?.restaurantLogoUrl) {
        setRestaurantLogoUrl(response.data.restaurantLogoUrl);
      } else {
        setRestaurantLogoUrl('');
      }
      // Restaurant logo shape
      if (response.data?.restaurantLogoShape) {
        setRestaurantLogoShape(response.data.restaurantLogoShape);
      } else {
        setRestaurantLogoShape('circle');
      }
    } catch (error) {
      console.warn('Failed to fetch QR ordering config:', error);
      setQrOrderingEnabled(true);
      setQrFormAllowSkip(true);
      setQrMenuShowPhotos(true);
      setRestaurantLogoUrl('');
      setRestaurantLogoShape('circle');
    } finally {
      setQrConfigLoaded(true);
    }
  };

  const fetchMenu = async () => {
    try {
      setMenuLoading(true);
      setMenuError('');
      const params = tableId ? { tableId } : tenantSlug ? { tenantSlug } : {};
      const [categoriesRes, itemsRes] = await Promise.all([
        api.get('/api/menu/categories', { params }),
        api.get('/api/menu', { params: { ...params, availableOnly: true } })
      ]);
      setCategories(categoriesRes.data?.categories || []);
      setItems(itemsRes.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      setCategories([]);
      setItems([]);
      setMenuError('Menu not found. Please check the QR link or contact the restaurant.');
    } finally {
      setMenuLoading(false);
    }
  };

  // Fetch existing active orders for this table so guests see their history even after reload
  const fetchExistingOrders = async () => {
    try {
      if (!tableId) return;

      const response = await api.get('/api/orders/public', {
        params: { tableId }
      });

      const orders = response.data?.orders || [];

      if (!orders.length) {
        setLastOrderSummary(null);
        return;
      }

      const itemMap = new Map();

      orders.forEach((order) => {
        (order.order_items || []).forEach((orderItem) => {
          const menuItem = orderItem.menu_items;
          if (!menuItem) return;

          const id = menuItem.id;
          const existing = itemMap.get(id) || {
            id,
            name: menuItem.name,
            price: menuItem.price,
            quantity: 0
          };

          existing.quantity += orderItem.quantity || 0;
          itemMap.set(id, existing);
        });
      });

      const aggregatedItems = Array.from(itemMap.values());

      if (aggregatedItems.length === 0) {
        setLastOrderSummary(null);
        // Clear stored history if backend says no active orders (e.g., after bill)
        try {
          if (tableId) {
            localStorage.removeItem(`qrOrderHistory_${tableId}`);
          }
        } catch {}
        return;
      }

      const summary = {
        items: aggregatedItems,
        placedAt: new Date().toISOString()
      };

      setLastOrderSummary(summary);

      // Persist to localStorage for this device
      try {
        if (tableId) {
          localStorage.setItem(`qrOrderHistory_${tableId}`, JSON.stringify(summary));
        }
      } catch {}
    } catch (error) {
      console.warn('Failed to fetch existing orders for table:', error);
      // Don't block menu rendering if this fails
    }
  };

  const fetchTableNumber = async () => {
    if (!tableId) {
      setLoadingTable(false);
      return;
    }
    try {
      setLoadingTable(true);
      const response = await api.get(`/api/tables/${tableId}`);
      console.log('Table response:', response.data);
      const fetchedTableNumber = response.data?.table_number;
      console.log('Fetched table number:', fetchedTableNumber);
      if (fetchedTableNumber) {
        setTableNumber(fetchedTableNumber);
        console.log('Table number set to:', fetchedTableNumber);
      } else {
        console.warn('Table number not found in response:', response.data);
        // Try to get from table_number field directly
        const altTableNumber = response.data?.table?.table_number || response.data?.table_number;
        if (altTableNumber) {
          setTableNumber(altTableNumber);
        } else {
          setTableNumber(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch table number:', error);
      console.error('Error details:', error.response?.data);
      console.error('Table ID used:', tableId);
      setTableNumber(null);
    } finally {
      setLoadingTable(false);
    }
  };

  const handleCallWaiter = async () => {
    if (callingWaiter || waiterCalled || !tableId) return;
    
    // Ensure we have table number before calling
    if (!tableNumber && !loadingTable) {
      await fetchTableNumber();
    }
    
    try {
      setCallingWaiter(true);
      await api.post('/api/waiter-calls/public', {
        tableId: tableId
      });
      setWaiterCalled(true);
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }
      // Reset after 30 seconds to allow calling again
      setTimeout(() => {
        setWaiterCalled(false);
      }, 30000);
    } catch (error) {
      console.error('Failed to call waiter:', error);
      await alert('Failed to call waiter. Please try again.', 'Error', 'error');
    } finally {
      setCallingWaiter(false);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    // Clear search when entering category view
    setSearchTerm('');
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSearchTerm('');
  };

  const handleChefsSpecialClick = (item) => {
    if (!item?.category_id) return;
    setSelectedCategory(item.category_id);
    setSearchTerm('');
    setPendingScrollItemId(item.id);
  };

  useEffect(() => {
    if (!pendingScrollItemId || !selectedCategory) return;

    const elementId = `menu-item-${pendingScrollItemId}`;

    const scrollToItem = () => {
      const targetElement = document.getElementById(elementId);
      if (!targetElement) return false;

      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      setPendingScrollItemId(null);
      return true;
    };

    if (scrollToItem()) return;

    const timeout = setTimeout(scrollToItem, 120);
    return () => clearTimeout(timeout);
  }, [pendingScrollItemId, selectedCategory, items.length, searchTerm, vegFilter]);

  const addToCart = async (item) => {
    if (!orderingAllowed) {
      return;
    }
    // Merge by dish: one line per item id, with a single special instruction field
    setCart((prev) => {
      const idx = prev.findIndex((line) => line.id === item.id);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          quantity: (updated[idx].quantity || 0) + 1,
        };
        return updated;
      }
      return [
        ...prev,
        {
          ...item,
          quantity: 1,
          specialInstructions: '',
        },
      ];
    });
  };

  const removeFromCart = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, quantity) => {
    if (quantity <= 0) {
      removeFromCart(index);
    } else {
      setCart((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, quantity: Number(quantity) || 1 } : item
        )
      );
    }
  };

  const handlePlaceOrder = async () => {
    if (!orderingAllowed) {
      await alert('QR ordering is currently disabled for this restaurant.', 'Ordering Disabled', 'warning');
      return;
    }
    if (cart.length === 0) {
      await alert('Your cart is empty', 'Empty Cart', 'warning');
      return;
    }

    if (placingOrder) {
      // Protect against double-taps while a request is in flight
      return;
    }

    // Detect potential duplicate order (same items + quantities as last summary)
    if (lastOrderSummary && lastOrderSummary.items && lastOrderSummary.items.length > 0) {
      const normalize = (itemsArr) =>
        [...itemsArr]
          .map((i) => ({ id: i.id, quantity: Number(i.quantity || 0) }))
          .sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0));

      const current = normalize(
        cart.map((item) => ({ id: item.id, quantity: item.quantity }))
      );
      const previous = normalize(lastOrderSummary.items);

      const isSameLength = current.length === previous.length;
      const isSameItems =
        isSameLength &&
        current.every(
          (cur, idx) =>
            cur.id === previous[idx].id && cur.quantity === previous[idx].quantity
        );

      if (isSameItems) {
        const proceed = await confirm(
          'You already placed this exact order for this table. Do you want to place it again?',
          'Duplicate order detected',
          'warning'
        );
        if (!proceed) {
          return;
        }
      }
    }

    try {
      setPlacingOrder(true);
      let guestId = null;
      if (guestInfo.name && guestInfo.phone) {
        try {
          const guestResponse = await api.post('/api/guests/restaurant/public', {
            name: guestInfo.name,
            phone: guestInfo.phone,
            tableId: tableId // Pass tableId so tenant can be determined
          });
          guestId = guestResponse.data.guest?.id || guestResponse.data.id;
        } catch (error) {
          console.error('Failed to create guest:', error);
          // Don't fail the order if guest creation fails
        }
      }

      // One special instruction per dish: send exactly one line per cart item
      const itemsPayload = cart
        .map((item) => ({
          menuItemId: item.id,
          quantity: Number(item.quantity || 0),
          specialInstructions: (item.specialInstructions || '').trim(),
        }))
        .filter((i) => i.quantity > 0);

      const orderData = {
        tableId: tableId,
        guestId: guestId,
        guestType: 'restaurant',
        orderType: 'dine_in',
        items: itemsPayload,
      };

      const response = await api.post('/api/orders/public', orderData);
      console.log('Order placed successfully:', response.data);

      const orderedItems = cart.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price
      }));

      // Merge this order into the running summary so guest sees all orders from this device
      const mergedSummary = (() => {
        const prev = lastOrderSummary;
        if (!prev || !prev.items) {
          return {
            items: orderedItems,
            placedAt: new Date().toISOString()
          };
        }

        const itemMap = new Map();

        // Start with previous items
        prev.items.forEach((item) => {
          itemMap.set(item.id, { ...item });
        });

        // Add new items, increasing quantities if they already exist
        orderedItems.forEach((item) => {
          if (itemMap.has(item.id)) {
            const existing = itemMap.get(item.id);
            existing.quantity += item.quantity;
            itemMap.set(item.id, existing);
          } else {
            itemMap.set(item.id, { ...item });
          }
        });

        return {
          items: Array.from(itemMap.values()),
          placedAt: new Date().toISOString()
        };
      })();

      setLastOrderSummary(mergedSummary);

      // Persist merged summary locally so it survives reloads until bill is generated
      try {
        if (tableId) {
          localStorage.setItem(`qrOrderHistory_${tableId}`, JSON.stringify(mergedSummary));
        }
      } catch {}

      await alert('Order placed successfully! You will receive it shortly.', undefined, 'success');
      // Clear the cart and close the modal so user starts fresh
      setCart([]);
      setShowCart(false);

      // Refresh existing orders from the backend so the "Your order has been sent"
      // section reflects the latest orders without requiring a page reload
      await fetchExistingOrders();
    } catch (error) {
      console.error('Failed to place order:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to place order. Please try again.';
      await alert(errorMessage, 'Error', 'error');
    } finally {
      setPlacingOrder(false);
    }
  };

  // Filter items based on search and veg filter
  const filteredItems = items.filter(item => {
    const matchesSearch = !searchTerm || 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const isVegItem = item.is_vegetarian === true;
    const isDietaryCommon = item.is_dietary_common === true;
    
    const matchesVegFilter = 
      vegFilter === 'all' ||
      (vegFilter === 'veg' && (isVegItem || isDietaryCommon)) ||
      (vegFilter === 'non-veg' && (!isVegItem || isDietaryCommon));
    
    return matchesSearch && matchesVegFilter;
  });

  // Get items for selected category
  const selectedCategoryItems = selectedCategory 
    ? filteredItems.filter(item => item.category_id === selectedCategory)
    : [];

  // Get categories with items count
  const categoriesWithItems = categories.map(category => ({
    ...category,
    itemCount: filteredItems.filter(item => item.category_id === category.id).length
  })).filter(category => category.itemCount > 0);

  // Chef's Special items (available items marked as chef's special)
  const chefsSpecialItems = filteredItems.filter(
    (item) => item.is_chefs_special && item.is_available !== false
  );

  const formatMenuPrice = (item, decimals = 2) => {
    if (item?.is_apc) {
      return 'APC';
    }
    return `₹${parseFloat(item.price).toFixed(decimals)}`;
  };

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <div className="min-h-screen bg-white pb-28">
      {/* Guest Info Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl transform animate-slideUp relative">
            {/* Close button - only show if allowSkip is true */}
            {qrFormAllowSkip && (
              <button
                onClick={() => setShowGuestModal(false)}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900"
                aria-label="Close"
              >
                <FiX className="w-5 h-5" />
              </button>
            )}
            
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-900 to-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                <span className="text-4xl">👋</span>
              </div>
              <p className="text-gray-600 text-sm">Please provide your details to continue</p>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={guestInfo.name}
                  onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-base transition-all bg-gray-50 focus:bg-white"
                  placeholder="Enter your name"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={guestInfo.phone}
                  onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-base transition-all bg-gray-50 focus:bg-white"
                  placeholder="Enter your phone number"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                {qrFormAllowSkip && (
                  <button
                    onClick={() => setShowGuestModal(false)}
                    className="flex-1 px-5 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 font-semibold text-base transition-all"
                  >
                    Skip
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (guestInfo.name && guestInfo.phone) {
                      setShowGuestModal(false);
                    } else {
                      await alert('Please fill in all required fields', 'Validation Error', 'warning');
                    }
                  }}
                  className={`${qrFormAllowSkip ? 'flex-1' : 'w-full'} px-5 py-4 bg-gray-900 text-white rounded-xl hover:bg-gray-800 active:scale-[0.98] font-semibold text-base transition-all shadow-lg`}
                >
                  Continue to Menu →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header - Restaurant Logo & Actions */}
      <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-black/10">
        <div className="px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Restaurant Logo (from settings) */}
            <div
              className={`w-14 h-14 border border-black/5 bg-white flex items-center justify-center overflow-hidden flex-shrink-0 ${
                restaurantLogoShape === 'square'
                  ? 'rounded-xl'
                  : restaurantLogoShape === 'rounded'
                  ? 'rounded-2xl'
                  : 'rounded-full'
              }`}
            >
              {restaurantLogoUrl ? (
                <img
                  src={restaurantLogoUrl}
                  alt="Restaurant logo"
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-sm font-semibold">Menu</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {selectedCategory && (
                  <button
                    onClick={handleBackToCategories}
                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-black text-white hover:bg-gray-900 transition-all shadow-sm flex-shrink-0"
                    title="Back to categories"
                  >
                    <FiArrowLeft className="text-sm" />
                  </button>
                )}
                <h1 className="text-xl font-semibold truncate">
                  {selectedCategory 
                    ? categories.find(c => c.id === selectedCategory)?.name || 'Our Menu'
                    : 'Our Menu'}
                </h1>
              </div>
              <p className="text-gray-500 text-xs mt-0.5 truncate">
                {loadingTable ? 'Loading table...' : tableNumber ? `Table ${tableNumber}` : tableId ? `Table ${tableId.slice(0, 8)}...` : 'Table'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Search Toggle Button - Only show in category view */}
            {!selectedCategory && (
              <button
                onClick={() => setShowSearch(!showSearch)}
                className={`w-9 h-9 flex items-center justify-center rounded-xl border border-black/10 bg-white transition-all shadow-sm ${
                  showSearch ? 'bg-black text-white' : 'hover:bg-gray-50'
                }`}
                title="Search menu"
              >
                <FiSearch className="text-base" />
              </button>
            )}
            {/* Call Waiter Button */}
            <button
              onClick={handleCallWaiter}
              disabled={callingWaiter || waiterCalled}
              className={`w-9 h-9 flex items-center justify-center rounded-xl border border-black/10 transition-all shadow-sm ${
                waiterCalled
                  ? 'bg-green-100 text-green-700'
                  : callingWaiter
                  ? 'bg-gray-100 text-gray-600'
                  : 'bg-white hover:bg-gray-50 text-gray-800'
              }`}
              title={waiterCalled ? "Waiter called" : "Call Waiter"}
            >
              <FiBell className="text-base" />
            </button>
            {orderingAllowed && cart.length > 0 && (
              <button
                onClick={() => setShowCart(true)}
                className="relative w-9 h-9 flex items-center justify-center rounded-xl border border-black/10 bg-black text-white hover:bg-gray-900 transition-all shadow-sm"
              >
                <FiShoppingCart className="text-base" />
                <span className="absolute -top-1 -right-1 bg-white text-black rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold shadow">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Search Bar - Only show in category view */}
        {!selectedCategory && showSearch && (
          <div className="px-4 pb-3 animate-fadeIn">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-10 py-2.5 bg-white border border-black/10 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all shadow-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-all"
                >
                  <FiX className="text-sm" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Veg/Non-Veg Filter Dropdown - Only show in category view */}
        {!selectedCategory && (
          <div className="px-4 pb-3 flex items-center gap-3 bg-white">
            <div className="flex items-center gap-2 text-gray-600">
              <FiFilter className="text-sm" />
              <span className="text-xs font-medium">Filter:</span>
            </div>
            <div className="relative flex-1">
              <select
                value={vegFilter}
                onChange={(e) => setVegFilter(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-black/10 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all appearance-none cursor-pointer"
              >
                <option value="all">All Items</option>
                <option value="veg">Vegetarian Only</option>
                <option value="non-veg">Non-Vegetarian Only</option>
              </select>
              <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
            </div>
          </div>
        )}
      </header>

      {qrConfigLoaded && !orderingAllowed && (
        <div className="mx-4 mt-4 rounded-2xl border border-black bg-white px-4 py-3 text-center shadow-sm">
          <p className="text-sm font-semibold text-black">QR ordering is currently disabled.</p>
          <p className="text-xs text-black/60">You can still browse the menu from this link.</p>
        </div>
      )}

      {menuError && (
        <div className="mx-4 mt-4 rounded-2xl border border-black bg-white px-4 py-3 text-center shadow-sm">
          <p className="text-sm font-semibold text-black">{menuError}</p>
        </div>
      )}

      {/* Category View or Dish View */}
      {!selectedCategory ? (
        /* Categories View - Full Screen Cards */
        <div className="px-4 py-6">
          {/* Powered by text */}
          <p className="text-[11px] text-gray-400 font-medium mb-3 text-right">
            Powered by <span className="font-semibold text-gray-500">Elyspr</span>
          </p>

          {/* Last order summary for this session (also mirrored in cart modal) */}
          {lastOrderSummary && lastOrderSummary.items?.length > 0 && (
            <div className="mb-4 rounded-2xl border border-black/10 bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-black">Your order has been sent</p>
                <span className="text-[11px] font-semibold text-green-600">ORDER SENT SUCCESSFULLY</span>
              </div>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {lastOrderSummary.items.map((item) => (
                  <li key={item.id} className="flex items-center justify-between text-xs text-black/80">
                    <span className="truncate pr-2">{item.name}</span>
                    <span className="font-semibold">x{item.quantity}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {menuLoading ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-14 h-14 border-4 border-black/20 border-t-black rounded-full animate-spin" />
              </div>
              <p className="text-black text-lg font-semibold mb-2">Loading menu...</p>
              <p className="text-black/60 text-sm">Please wait</p>
            </div>
          ) : (searchTerm || vegFilter !== 'all') && filteredItems.length === 0 ? (
            <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiSearch className="text-4xl text-white" />
              </div>
              <p className="text-black text-lg font-semibold mb-2">No items found</p>
              <p className="text-black/60 text-sm mb-4">Try adjusting your search or filters</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setVegFilter('all');
                }}
                className="bg-black text-white px-6 py-2 rounded-xl hover:bg-gray-900 font-medium transition-all"
              >
                Clear Filters
              </button>
            </div>
          ) : categoriesWithItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🍽️</span>
              </div>
              <p className="text-black text-lg font-semibold mb-2">Menu is being prepared</p>
              <p className="text-black/60 text-sm">Please check back soon</p>
            </div>
          ) : (
            searchTerm.trim() ? (
              <div className="space-y-3">
                {filteredItems.map((item) => {
                  const cartItem = cart.find((c) => c.id === item.id);
                  const quantity = cartItem?.quantity || 0;
                  const cartIndex = cart.findIndex((c) => c.id === item.id);
                  const categoryName = categories.find((c) => c.id === item.category_id)?.name || 'Menu Item';

                  return (
                    <div key={item.id} className="bg-white rounded-2xl border border-black/10 shadow-sm p-3">
                      <div className="flex items-center gap-3">
                        {qrMenuShowPhotos && (
                          <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-2xl">🍽️</div>
                            )}
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-black/50 font-medium mb-0.5">{categoryName}</p>
                          <h3 className="font-semibold text-black truncate">{item.name}</h3>
                          <p className="text-sm text-black/80 mt-0.5">{formatMenuPrice(item, 2)}</p>
                        </div>

                        {orderingAllowed && (
                          <div className="flex items-center gap-2">
                            {quantity > 0 ? (
                              <>
                                <button
                                  onClick={() => cartIndex !== -1 && updateQuantity(cartIndex, quantity - 1)}
                                  className="bg-black text-white w-8 h-8 rounded-full flex items-center justify-center"
                                >
                                  <FiMinus className="text-sm" />
                                </button>
                                <span className="text-sm font-semibold min-w-[18px] text-center">{quantity}</span>
                                <button
                                  onClick={() => cartIndex !== -1 && updateQuantity(cartIndex, quantity + 1)}
                                  className="bg-black text-white w-8 h-8 rounded-full flex items-center justify-center"
                                >
                                  <FiPlus className="text-sm" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => addToCart(item)}
                                className="bg-black text-white w-9 h-9 rounded-full flex items-center justify-center"
                              >
                                <FiPlus className="text-base" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-6">
                {categoriesWithItems.map((category) => {
                  const categoryItems = filteredItems.filter(item => item.category_id === category.id);
                  // Use category image_url if available, otherwise fallback to first item's image
                  const categoryImage = category.image_url || categoryItems.find(item => item.image_url)?.image_url;

                  return (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category.id)}
                      className="w-full relative overflow-hidden rounded-3xl shadow-xl border border-black transition-all hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] bg-white"
                    >
                      {qrMenuShowPhotos ? (
                        <div className="relative h-48 w-full bg-gray-100">
                          {categoryImage ? (
                            <img
                              src={categoryImage}
                              alt={category.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              <span className="text-6xl">🍽️</span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"></div>
                          <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
                            <h2 className="text-2xl font-bold text-white mb-1">{category.name}</h2>
                            <p className="text-white/90 text-sm font-medium">
                              {category.itemCount} {category.itemCount === 1 ? 'item' : 'items'} available
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="px-6 py-5 text-left bg-gradient-to-r from-white to-gray-50">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <h2 className="text-xl font-bold text-black mb-1">{category.name}</h2>
                              <p className="text-black/70 text-sm font-medium">
                                {category.itemCount} {category.itemCount === 1 ? 'item' : 'items'} available
                              </p>
                            </div>
                            <FiChevronRight className="text-xl text-black/60" />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}

                {/* Chef's Special Carousel - static, equal-sized cards */}
                {chefsSpecialItems.length > 0 && (
                  <div className="mt-12">
                    <div className="mb-5 text-center px-6">
                      <h2 className="text-3xl font-extrabold text-black tracking-wide">
                        CHEF'S SPECIAL
                      </h2>
                      <p className="mt-3 text-sm text-black/60 italic max-w-md mx-auto leading-snug">
                        "Food is our common ground, a universal experience." — James Beard
                      </p>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-4 px-4 snap-x snap-mandatory justify-center scroll-smooth">
                      {chefsSpecialItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleChefsSpecialClick(item)}
                          className="snap-center flex-shrink-0 rounded-3xl overflow-hidden border border-black/10 bg-[#f5e8d8] shadow-md min-w-[240px] max-w-[240px]"
                          title={`Go to ${item.name}`}
                        >
                          {qrMenuShowPhotos && (
                            <div className="w-full h-44 overflow-hidden">
                              {item.image_url ? (
                                <img
                                  src={item.image_url}
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                  <span className="text-4xl">🍽️</span>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="px-4 py-3">
                            <p className="text-[11px] uppercase tracking-[0.2em] text-black/60 mb-1">
                              Food
                            </p>
                            <h3 className="font-semibold text-black mb-1 line-clamp-1 text-base">
                              {item.name}
                            </h3>
                            <p className="font-medium text-black text-sm">
                              {formatMenuPrice(item, 0)}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      ) : (
        /* Dish View - Individual Dishes with Images */
        <div className="px-4 py-6 space-y-6">
          {selectedCategoryItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🍽️</span>
              </div>
              <p className="text-black text-lg font-semibold mb-2">No items found</p>
              <p className="text-black/60 text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            selectedCategoryItems.map((item) => {
              const cartItem = cart.find(c => c.id === item.id);
              const quantity = cartItem?.quantity || 0;
              const cartIndex = cart.findIndex(c => c.id === item.id);

              return (
                <div
                  key={item.id}
                  id={`menu-item-${item.id}`}
                  className="bg-white rounded-3xl shadow-xl border border-black overflow-hidden transition-all hover:shadow-2xl"
                >
                  {qrMenuShowPhotos ? (
                    <div className="relative h-80 w-full bg-gray-100">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <span className="text-7xl">🍽️</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>

                      {orderingAllowed && (
                        <div className="absolute top-4 right-4 z-10">
                          {quantity > 0 ? (
                            <div className="flex items-center gap-2 bg-white rounded-full px-3 py-2 shadow-xl">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (cartIndex !== -1) {
                                    updateQuantity(cartIndex, quantity - 1);
                                  }
                                  if (navigator.vibrate) navigator.vibrate(30);
                                }}
                                className="bg-black text-white w-8 h-8 rounded-full flex items-center justify-center active:bg-gray-800 transition-all font-bold"
                              >
                                <FiMinus className="text-sm" />
                              </button>
                              <span className="font-bold text-base text-black min-w-[24px] text-center">{quantity}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (cartIndex !== -1) {
                                    updateQuantity(cartIndex, quantity + 1);
                                  }
                                  if (navigator.vibrate) navigator.vibrate(30);
                                }}
                                className="bg-black text-white w-8 h-8 rounded-full flex items-center justify-center active:bg-gray-800 transition-all font-bold"
                              >
                                <FiPlus className="text-sm" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(item);
                                if (navigator.vibrate) {
                                  navigator.vibrate(50);
                                }
                              }}
                              className="bg-white text-black w-12 h-12 rounded-full flex items-center justify-center hover:bg-gray-100 active:scale-95 transition-all shadow-xl border-2 border-black"
                            >
                              <FiPlus className="text-xl font-bold" />
                            </button>
                          )}
                        </div>
                      )}

                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        {item.description && (
                          <p className="text-white text-sm font-medium leading-relaxed mb-3 line-clamp-2">
                            {item.description}
                          </p>
                        )}

                        <h3 className="text-2xl font-bold text-white mb-2">{item.name}</h3>

                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex flex-wrap gap-2">
                            {item.is_vegetarian && (
                              <span className="inline-flex items-center bg-white/90 backdrop-blur-sm border border-white text-black text-xs px-3 py-1 rounded-full font-semibold">
                                Vegetarian
                              </span>
                            )}
                            {item.spice_level > 0 && (
                              <span className="inline-flex items-center bg-white/90 backdrop-blur-sm border border-white text-black text-xs px-3 py-1 rounded-full font-semibold">
                                Spicy {item.spice_level}
                              </span>
                            )}
                            {item.preparation_time && (
                              <span className="inline-flex items-center bg-white/90 backdrop-blur-sm border border-white text-black text-xs px-3 py-1 rounded-full font-semibold">
                                {item.preparation_time} min
                              </span>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-2xl font-bold text-white">{formatMenuPrice(item, 2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-black">{item.name}</h3>
                          <p className="text-lg font-semibold text-black mt-1">{formatMenuPrice(item, 2)}</p>
                        </div>
                        {orderingAllowed && (
                          <div className="flex items-center gap-2">
                            {quantity > 0 ? (
                              <div className="flex items-center gap-2 bg-white rounded-full px-2 py-1 shadow border border-black/10">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (cartIndex !== -1) {
                                      updateQuantity(cartIndex, quantity - 1);
                                    }
                                    if (navigator.vibrate) navigator.vibrate(30);
                                  }}
                                  className="bg-black text-white w-8 h-8 rounded-full flex items-center justify-center active:bg-gray-800 transition-all font-bold"
                                >
                                  <FiMinus className="text-sm" />
                                </button>
                                <span className="font-bold text-base text-black min-w-[24px] text-center">{quantity}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (cartIndex !== -1) {
                                      updateQuantity(cartIndex, quantity + 1);
                                    }
                                    if (navigator.vibrate) navigator.vibrate(30);
                                  }}
                                  className="bg-black text-white w-8 h-8 rounded-full flex items-center justify-center active:bg-gray-800 transition-all font-bold"
                                >
                                  <FiPlus className="text-sm" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToCart(item);
                                  if (navigator.vibrate) {
                                    navigator.vibrate(50);
                                  }
                                }}
                                className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-900 active:scale-95 transition-all shadow"
                              >
                                <FiPlus className="text-lg font-bold" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {item.description && (
                        <p className="text-black/70 text-sm leading-relaxed mb-3">{item.description}</p>
                      )}

                      <div className="flex flex-wrap gap-2">
                        {item.is_vegetarian && (
                          <span className="inline-flex items-center border border-black/10 text-black text-xs px-3 py-1 rounded-full font-semibold bg-white">
                            Vegetarian
                          </span>
                        )}
                        {item.spice_level > 0 && (
                          <span className="inline-flex items-center border border-black/10 text-black text-xs px-3 py-1 rounded-full font-semibold bg-white">
                            Spicy {item.spice_level}
                          </span>
                        )}
                        {item.preparation_time && (
                          <span className="inline-flex items-center border border-black/10 text-black text-xs px-3 py-1 rounded-full font-semibold bg-white">
                            {item.preparation_time} min
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Cart Button - Mobile Optimized */}
      {orderingAllowed && cart.length > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-black text-white p-5 rounded-full shadow-2xl hover:bg-gray-900 active:scale-90 flex items-center gap-3 z-40 transition-all animate-bounce-subtle"
        >
          <FiShoppingCart className="text-2xl" />
          <span className="bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg border border-black">
            {cart.reduce((sum, item) => sum + item.quantity, 0)}
          </span>
        </button>
      )}

      {/* Cart Modal - Mobile Optimized */}
      {orderingAllowed && showCart && (
        <div className="fixed inset-0 bg-black/30 z-50 backdrop-blur-sm animate-fadeIn" onClick={() => setShowCart(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white shadow-2xl overflow-y-auto rounded-t-3xl max-h-[90vh] transform animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white text-black border-b border-black/10 p-6 flex justify-between items-center z-10">
              <div>
                <h2 className="text-2xl font-bold">Your Order</h2>
                {cart.length > 0 && (
                  <p className="text-black/60 text-sm mt-1">
                    {cart.reduce((sum, item) => sum + item.quantity, 0)}{' '}
                    {cart.reduce((sum, item) => sum + item.quantity, 0) === 1 ? 'item' : 'items'} in order
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowCart(false)}
                className="text-black hover:text-black/80 p-2 transition-colors bg-black/5 rounded-full hover:bg-black/10"
              >
                <FiX className="text-2xl" />
              </button>
            </div>
            <div className="p-6 space-y-5 pb-40">
              {cart.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiShoppingCart className="text-4xl text-white" />
                  </div>
                  <p className="text-black text-lg font-semibold mb-2">Your cart is empty</p>
                  <p className="text-black/60 text-sm">Add items from the menu</p>
                </div>
              ) : cart.length > 0 ? (
                cart.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    className="flex items-center gap-4 bg-white rounded-2xl p-4 border border-black/10 shadow-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-black text-base mb-1 line-clamp-1">
                        {item.name}
                      </h3>
                      <div className="flex items-center gap-3 mb-2">
                        <button
                          onClick={() => {
                            updateQuantity(index, item.quantity - 1);
                            if (navigator.vibrate) navigator.vibrate(30);
                          }}
                          className="bg-black text-white w-9 h-9 rounded-full flex items-center justify-center active:bg-gray-800 transition-all shadow-sm border border-black font-bold"
                        >
                          <FiMinus className="text-sm" />
                        </button>
                        <span className="font-bold text-lg text-black min-w-[28px] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => {
                            updateQuantity(index, item.quantity + 1);
                            if (navigator.vibrate) navigator.vibrate(30);
                          }}
                          className="bg-black text-white w-9 h-9 rounded-full flex items-center justify-center active:bg-gray-800 transition-all shadow-sm border border-black font-bold"
                        >
                          <FiPlus className="text-sm" />
                        </button>
                      </div>
                      <div className="space-y-1 pt-1">
                        <span className="text-[11px] font-semibold text-black/70">
                          Special instructions (optional)
                        </span>
                        <textarea
                          rows={2}
                          placeholder="E.g. no onion, extra cheese, less spicy..."
                          value={item.specialInstructions || ''}
                          onChange={(e) =>
                            setCart(
                              cart.map((c, i) =>
                                i === index ? { ...c, specialInstructions: e.target.value } : c
                              )
                            )
                          }
                          className="w-full mt-0.5 px-3 py-2 border border-black/20 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-black resize-none bg-white"
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : null}
            </div>
            {cart.length > 0 && (
              <div className="sticky bottom-0 bg-white border-t border-black/10 p-6 shadow-2xl">
                <button
                  onClick={handlePlaceOrder}
                  className="w-full bg-black text-white py-4 rounded-2xl hover:bg-gray-900 active:scale-98 font-bold text-lg transition-all shadow-xl"
                >
                  Place Order →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default QRMenu;
